import React, { ReactNode, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { createInstance, OptimizelyProvider, enums } from '@optimizely/react-sdk';
import { useRouter } from 'next/router';
import { OPTIMIZELY_DATAFILE } from 'config';
import getIsVipUser from 'lib/getIsVipUser';
import getUserGroup from 'lib/getUserGroup';
import getOptimizelyUserId from 'lib/getOptimizelyUserId';
import { optimizelyFeatureDecision } from 'store/ui/uiActions';

interface OptimizelyDecisionPayload {
  userId: string;
  decisionInfo: {
    flagKey: string;
    ruleKey: string;
    variationKey: string;
    enabled: boolean;
  };
}

export const OptimizelyProviderWrapper = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { isReady } = useRouter();
  const optimizelyUserId = getOptimizelyUserId();
  const ghUserGroup = getUserGroup();
  const isVipUser = getIsVipUser();

  const processedFlagsRef = useRef(new Set<string>());

  const optimizely = createInstance({
    eventBatchSize: 100,
    eventFlushInterval: 3000,
    odpOptions: {
      disabled: true,
    },
    datafileOptions: {
      autoUpdate: true,
      updateInterval: 60000,
    },
    sdkKey: OPTIMIZELY_DATAFILE,
    logLevel: 'none',
  });

  useEffect(() => {
    if (isReady && optimizelyUserId) {
      const listenerId = optimizely.notificationCenter.addNotificationListener(
        enums.NOTIFICATION_TYPES.DECISION,
        (payload: OptimizelyDecisionPayload) => {
          const { decisionInfo } = payload;
          const { flagKey, ruleKey, variationKey, enabled } = decisionInfo;
          const triggerEvent = flagKey === 'qantas-hotels-available-rooms-message';

          // use processedFlagsRef to avoid the event to be fired more than once
          if (triggerEvent && !processedFlagsRef.current.has(flagKey)) {
            const analyticsPayload = {
              flagKey: flagKey,
              variationKey: variationKey,
              ruleKey: ruleKey,
              enabled: !!enabled,
            };

            dispatch(optimizelyFeatureDecision(analyticsPayload));
            processedFlagsRef.current.add(flagKey);
          }
        },
      );
      return () => {
        optimizely.notificationCenter.removeNotificationListener(listenerId);
      };
    }
  }, [isReady, optimizelyUserId, optimizely, dispatch]);

  if (isReady && optimizelyUserId) {
    return (
      <OptimizelyProvider
        optimizely={optimizely}
        user={{
          id: optimizelyUserId,
          attributes: {
            user_group: ghUserGroup,
            is_vip: isVipUser,
          },
        }}
      >
        {children}
      </OptimizelyProvider>
    );
  } else {
    return <>{children}</>;
  }
};
